import { useEffect, useMemo } from 'react'
import { useForm, useFieldArray, Controller, useWatch } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useOnboardingStore, STEPS } from '../stores/onboardingStore'
import { useStepSubmit } from '../context/StepSubmitContext'
import RowInput from '../components/form/RowInput'
import EmailInputWithDomain from '../components/form/EmailInputWithDomain'
import AddAnotherButton from '../components/form/AddAnotherButton'
import EntryHeader from '../components/form/EntryHeader'
import ChipToggleGroup from '../components/form/ChipToggleGroup'
import Field from '../components/form/Field'
import FieldRow from '../components/form/FieldRow'
import Card from '../components/form/Card'
import StepLayout from '../components/form/StepLayout'
import Band from '../components/form/Band'
import ErrorBanner from '../components/form/ErrorBanner'
import { useStepErrorSummary } from '../hooks/useStepErrorSummary'
import { extractEmailDomain, ordinalLabel, personalizeTitle } from '../lib/formatters'
import {
  flattenErrorCount,
  isValidEmail,
  suggestEmailCorrection,
  uniqueEmailsAcross,
  validateName,
} from '../lib/validators'
import { isValidationDisabled } from '../lib/validationMode'

const STEP_ID = 'providers'

const buildStrictSchema = (isNetworkPlus) => {
  const isRowEmpty = (p) => {
    const name = String(p?.fullName ?? '').trim()
    const email = String(p?.email ?? '').trim()
    const locs = Array.isArray(p?.locationIds) ? p.locationIds : []
    return name === '' && email === '' && locs.length === 0
  }
  return z
    .object({
      providers: z.preprocess(
        (val) => {
          if (!Array.isArray(val)) return val
          return isNetworkPlus ? val.filter((p) => !isRowEmpty(p)) : val
        },
        z
          .array(
            z.object({
              fullName: z.string().refine((v) => validateName(v).isValid, {
                message: 'Add a full name (first and last)',
              }),
              email: z.string().refine(isValidEmail, {
                message: 'Add a work email',
              }),
              locationIds: z
                .array(z.string())
                .min(1, 'Select at least one location'),
            }),
          )
          .min(isNetworkPlus ? 0 : 1, 'Add at least one provider'),
      ),
    })
    .superRefine((data, ctx) => {
      const seen = new Map()
      data.providers.forEach((p, i) => {
        const key = String(p?.email ?? '').trim().toLowerCase()
        if (!key) return
        if (seen.has(key)) {
          ctx.addIssue({
            code: 'custom',
            path: ['providers', i, 'email'],
            message: 'Each provider needs a unique email',
          })
        } else {
          seen.set(key, i)
        }
      })
    })
}
const looseSchema = z.object({
  providers: z.array(z.any()).optional(),
}).passthrough()

const emptyProvider = { fullName: '', email: '', locationIds: [] }

function buildLocationOptions(locations) {
  if (!Array.isArray(locations)) return []
  return locations
    .filter((l) => l?.id)
    .map((l, i) => {
      const label = (l?.addressLine1 || '').trim() || `Location ${i + 1}`
      return { value: l.id, label }
    })
}

export default function Step6Providers() {
  const step = STEPS.find((s) => s.id === 'providers')
  const practiceName = useOnboardingStore(
    (s) => s.formData?.details?.practiceName,
  )
  const isNetworkPlus = useOnboardingStore(
    (s) => s.formData?.networkplus?.isMemberPlusMember === true,
  )
  const liteMode = useOnboardingStore((s) => s.liteMode)
  const persisted = useOnboardingStore((s) => s.formData[STEP_ID])
  const locationsData = useOnboardingStore(
    (s) => s.formData.locations?.locations,
  )
  const suggested = useOnboardingStore((s) => s.formData?.suggested)
  const practiceWebsite = useOnboardingStore(
    (s) => s.formData?.details?.website,
  )
  const suggestedDomain =
    suggested?.emailDomain || extractEmailDomain(practiceWebsite) || null
  const updateFormData = useOnboardingStore((s) => s.updateFormData)
  const nextStep = useOnboardingStore((s) => s.nextStep)
  const teamsData = useOnboardingStore((s) => s.formData?.teams?.teams)
  const { setHandler, clearHandler, reportErrorCount, pulse, focusFirstError } =
    useStepSubmit()

  const locationOptions = useMemo(
    () => buildLocationOptions(locationsData),
    [locationsData],
  )

  const defaultValues = useMemo(
    () => ({
      providers:
        persisted?.providers && persisted.providers.length > 0
          ? persisted.providers
          : isNetworkPlus
            ? []
            : [emptyProvider],
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )

  const schema = useMemo(
    () =>
      isValidationDisabled() ? looseSchema : buildStrictSchema(isNetworkPlus),
    [isNetworkPlus],
  )

  const {
    control,
    register,
    handleSubmit,
    setError,
    clearErrors,
    setValue,
    trigger,
    reset,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues,
    mode: 'onBlur',
    reValidateMode: 'onChange',
  })


  const { fields, append, remove } = useFieldArray({
    control,
    name: 'providers',
  })
  const watched = useWatch({ control, name: 'providers' })

  const onValid = (data) => {
    const cleaned = (data.providers || []).filter((p) => {
      const name = String(p?.fullName ?? '').trim()
      const email = String(p?.email ?? '').trim()
      const locs = Array.isArray(p?.locationIds) ? p.locationIds : []
      return name !== '' || email !== '' || locs.length > 0
    })
    updateFormData(STEP_ID, { providers: cleaned })
    nextStep()
  }
  const { submitAttempted, errorMessages, onInvalid } = useStepErrorSummary(
    STEP_ID,
    errors,
  )

  useEffect(() => {
    const submit = handleSubmit(onValid, onInvalid)
    setHandler(() => submit())
    return () => setHandler(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleSubmit, setHandler])

  useEffect(() => {
    reportErrorCount(flattenErrorCount(errors))
  }, [errors, reportErrorCount])

  useEffect(() => {
    return () => reportErrorCount(0)
  }, [reportErrorCount])

  useEffect(() => {
    if (persisted && Object.keys(persisted).length > 0) trigger()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Lite mode: keep store.providers in sync with the user's typed input so
  // StepNavigation can flip its label between "Skip" and "Continue" in real
  // time (skip vs continue is purely a UX cue — onValid still runs the same
  // cleanup + nextStep on submit).
  useEffect(() => {
    if (!liteMode) return
    const cleaned = (watched || []).filter((p) => {
      const name = String(p?.fullName ?? '').trim()
      const email = String(p?.email ?? '').trim()
      const locs = Array.isArray(p?.locationIds) ? p.locationIds : []
      return name !== '' || email !== '' || locs.length > 0
    })
    const current =
      useOnboardingStore.getState().formData?.providers?.providers || []
    if (current.length !== cleaned.length) {
      updateFormData(STEP_ID, { providers: cleaned })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watched, liteMode])

  // Cross-step email uniqueness: compare current providers against teams only.
  // Owner overlap is intentionally allowed (legit dual-role); the backend
  // silently aliases the owner email to +hub for the sheet row.
  useEffect(() => {
    const currentProviders = watched || []
    const dupes = uniqueEmailsAcross({
      providers: currentProviders,
      teams: teamsData,
    })
    currentProviders.forEach((p, i) => {
      const email = String(p?.email ?? '').trim().toLowerCase()
      if (!email) return
      const locations = dupes.get(email)
      if (!locations) {
        const cur = errors?.providers?.[i]?.email
        if (cur?.type === 'cross-step') clearErrors(`providers.${i}.email`)
        return
      }
      const other = locations.find(
        (l) => !(l.group === 'providers' && l.index === i),
      )
      if (!other) {
        const cur = errors?.providers?.[i]?.email
        if (cur?.type === 'cross-step') clearErrors(`providers.${i}.email`)
        return
      }
      setError(`providers.${i}.email`, {
        type: 'cross-step',
        message: 'Already used by team member. Each person needs their own email.',
      })
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watched, teamsData])

  return (
    <form className="step-body" onSubmit={handleSubmit(onValid, onInvalid)} noValidate>
      <StepLayout
        question={
          isNetworkPlus
            ? 'Add Your Own Providers (Optional)'
            : personalizeTitle('Who prescribes at {practice}?', { practiceName })
        }
        helper={
          liteMode
            ? "Skip this step if you don't have your own medical providers on staff. Add them here only if you do."
            : isNetworkPlus
              ? "Network+ gives you access to Dr. Rosensweet's provider network. Add your own providers here only if you have additional clinicians on staff."
              : step.subtitle
        }
      >
        <Band label="Providers" description="Clinicians who prescribe at your practice">
        <ErrorBanner show={submitAttempted} messages={errorMessages} />
        <EntryHeader noun="provider" count={fields.length} onAdd={() => append({ ...emptyProvider, locationIds: [] })} />
        <div className="provider-cards">
        {fields.map((f, idx) => {
          const rowErr = errors?.providers?.[idx]
          const emailVal = watched?.[idx]?.email
          const emailSuggestion = suggestEmailCorrection(emailVal)
          const label = ordinalLabel(idx, 'provider')
          const canRemove = fields.length > 1
          const removeButton = canRemove ? (
            <button
              type="button"
              className="provider-card-remove"
              aria-label="Remove this provider"
              onClick={() => remove(idx)}
            >
              ×
            </button>
          ) : null
          return (
            <Card key={f.id} title={label} actions={removeButton}>
              <FieldRow cols={2}>
                <Field label="Full name" error={rowErr?.fullName}>
                  <RowInput
                    type="text"
                    placeholder="Full legal name"
                    autoComplete="name"
                    error={!!rowErr?.fullName}
                    {...register(`providers.${idx}.fullName`)}
                  />
                </Field>

                <Field
                  label="Work email"
                  error={!emailSuggestion ? rowErr?.email : undefined}
                >
                  <Controller
                    name={`providers.${idx}.email`}
                    control={control}
                    render={({ field }) => (
                      <EmailInputWithDomain
                        value={field.value}
                        onChange={field.onChange}
                        suggestedDomain={suggestedDomain}
                        placeholder={suggestedDomain ? `you@${suggestedDomain}` : 'Work email'}
                        autoComplete="email"
                        error={!!rowErr?.email && !emailSuggestion}
                      />
                    )}
                  />
                  {emailSuggestion && (
                    <div className="email-suggestion">
                      Did you mean{' '}
                      <button
                        type="button"
                        className="email-suggestion-accept"
                        onClick={() =>
                          setValue(
                            `providers.${idx}.email`,
                            emailSuggestion,
                            { shouldValidate: true, shouldDirty: true },
                          )
                        }
                      >
                        {emailSuggestion}
                      </button>
                      ?
                    </div>
                  )}
                </Field>
              </FieldRow>

              <Field
                label="Locations this provider works at"
                error={rowErr?.locationIds}
              >
                <Controller
                  name={`providers.${idx}.locationIds`}
                  control={control}
                  render={({ field }) => (
                    <ChipToggleGroup
                      options={locationOptions}
                      value={field.value || []}
                      onChange={field.onChange}
                      emptyText="Add at least one location in the Locations step to assign providers."
                    />
                  )}
                />
              </Field>
            </Card>
          )
        })}
      </div>

        <AddAnotherButton
          label="Add another provider"
          onClick={() => append({ ...emptyProvider, locationIds: [] })}
        />
      </Band>
      </StepLayout>

      <button type="submit" className="sr-only" tabIndex={-1}>
        Continue
      </button>
    </form>
  )
}
